<template>
  <div
    tabindex="0"
    :data-id="objectModelTrigger.client_id"
    :data-type="objectModelTrigger.trigger_type"
    class="form-element"
    :class="propertyClasses"
    @focus="replaceIdInRoute"
    @click.stop="replaceIdInRoute">
    <row class="title-bar">
       <prefix>
          <span class="tag-icon" :class="ObjectModelTriggerDefinition.getIconColor(objectModelTrigger.trigger_type)">
            <icon :class="ObjectModelTriggerDefinition.getIcon(objectModelTrigger.trigger_type)"></icon>
          </span>
        </prefix>
        <column>
          <strong class="title">
            {{ObjectModelTriggerDefinition.getDisplayName(objectModelTrigger.trigger_type)}}
          </strong>
        </column>
        <move-destroy v-if="isMoveDestroyable"
                    :card="objectModelTrigger"
                    @remove:card="remove"
                    class="suffix"
      ></move-destroy>
        <suffix v-else>
          <icon class="locked"></icon>
          <span class="sr-only">Trigger Locked</span>
        </suffix>
    </row>
    <row class="component-row">
      <column v-if="objectModelTrigger.can(ObjectModelTriggerCapability.labelable)">
        <object-model-trigger-labelable
          :object-model-trigger="objectModelTrigger"></object-model-trigger-labelable>
      </column>
      <suffix>
        <button class="relative-container">
          <icon :class="[objectModelTrigger.notificationsArray.length > 0 ? 'notification-on' : 'notification-off']"></icon>
          <span class="notification-count">{{objectModelTrigger.notificationsArray.length}}</span>
          <span class="sr-only">Notification Bell Button</span>
        </button>&nbsp;
      </suffix>
    </row>

    <component
      :is="getComponentByType(objectModelTrigger.trigger_type)"
      :object-model-trigger="objectModelTrigger"
      @remove="remove"></component>

    <template v-if="objectModelTrigger.first_trigger.type === DurationType.propertyBasedTimeSpan">
      <p class="small">
        When it is {{
        `${objectModelTrigger.first_trigger.amount}
        ${durationUnitLabelFor(objectModelTrigger.first_trigger.unit)}
        ${comparisonLabelFor(objectModelTrigger.first_trigger.comparison)} ${objectModelPropertyLabel ? objectModelPropertyLabel + "," : ''}`}}
      </p>
    </template>
    <template v-if="objectModelTrigger.first_trigger.type === DurationType.datePicker">
      <p class="small">
        On {{triggerStartDate}}{{objectModelTrigger.occurs_at_interval ? "," : ""}}
      </p>
    </template>
    <template v-if="objectModelTrigger.occurs_at_interval">
      <p class="small">
        <interval-display :interval="objectModelTrigger.interval">
        </interval-display>
      </p>
    </template>
    <template v-if="objectModelTrigger.trigger_based_on_type === ObjectModelTriggerBasedOnType.ObjectModelProperty">
      <p class="small">
        <template v-for="(ruleSet, index) in objectModelTrigger.getSortedConditionalRuleSets('trigger')">
          <conditional-display-rule
            :key="`${ruleSet.client_id}`"
            :class="{ 'condition-rule': true }"
            :conditional-rule-set="ruleSet"
            :totalRuleSets="Object.keys(objectModelTrigger.trigger_conditional_rule_sets).length"
            hideControls
            :disabled="true">
          </conditional-display-rule>
          <slot name="value" :uniqueId="index" :model="ruleSet" fieldName="conditional_value"></slot>
        </template>
      </p>
    </template>
  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  import triggerBaseMixin from "../mixins/trigger-base-mixin";
  import routerMixin from "../../../common/mixins/routerMixin";
  import {Hash} from "../../../common/interfaces/hashInterfaces";
  import GloballySelected from "../store/modules/selected";
  import scrollToElement from "../../../common/helpers/scrollToElement";
  import DefaultTrigger from './trigger-default-trigger.vue';
  import CustomNotification from "./action-custom-notification.vue";
  import MoveDestroy from "../../../common/components/moveDestroy.vue";
  import ObjectModelTriggerLabelable from "./object-model-trigger-labelable.vue";
  import {ObjectModelTriggerCapability} from "~/models/objectModelTriggers/objectModelTriggerCapability";
  import ObjectModelTriggerDefinition from "~/models/objectModelTriggers/objectModelTriggerDefinition";
  import {ObjectModelTriggerType} from "~/enums/objectModelTriggerType";
  import AutoGenerateForm from "./trigger-auto-generate-form.vue"
  import ConditionalDisplayRule from "~/components/conditionalRuleBuilder/conditionalDisplayRule.vue"
  import IntervalDisplay from "~/components/interval-display.vue"
  import {DurationType} from "~/enums/durationType";
  import {ObjectModelTriggerBasedOnType} from "~/enums/objectModelTriggerBasedOnType";
  import DatetimeHelper from "~/helpers/datetimeHelper";
  import DurationAndTimeHelper from "~/helpers/durationAndTimeHelper";
  import {statusActivationDateComparisonVOptions} from "~/helpers/statusActivationHelper";
  import {DatetimeMode} from "~/models/datetimeMode";
  import selectedMixin from "../mixins/selectedMixin";
  import UpdatePropertyValues from "./trigger-update-property-values.vue"
  import RunMapping from "./trigger-run-mapping.vue";
  import RunProcess from "./trigger-run-process.vue";
  import RemoveRelationship from './trigger-remove-relationship.vue';
  import EstablishRelationship from './trigger-establish-relationship.vue';
  import {
    ObjectModelBuiltinPropertyType,
    ObjectModelBuiltinPropertyTypeName
  } from "~/enums/objectModelBuiltinPropertyType";
  export default Vue.extend({
    components: {
      "automatic-notification": DefaultTrigger,
      "auto-generate-form": AutoGenerateForm,
      "update-property-values": UpdatePropertyValues,
      "custom-notification": CustomNotification,
      "run-mapping":RunMapping,
      "remove-relationship": RemoveRelationship,
      "establish-relationship": EstablishRelationship,
      "run-process": RunProcess,
      ObjectModelTriggerLabelable,
      MoveDestroy,
      ConditionalDisplayRule,
      IntervalDisplay

    },
    mixins: [
      triggerBaseMixin,
      routerMixin,
      selectedMixin

    ],
    data: function() {
      return {
        ObjectModelTriggerCapability,
        ObjectModelTriggerDefinition,
        DurationType,
        ObjectModelTriggerBasedOnType
      };
    },
    mounted() {
      this.selectThisItem();
      if (this.objectModelTrigger.isSelected) {
        this.scrollToElement();
        if (this.$route.query['selectedId'] !== this.objectModelTrigger.client_id) {
          this.replaceIdInRoute();
        }
      }
    },
    computed: {
      propertyClasses(): Hash<boolean> {
        return {
          active: this.objectModelTrigger.isSelected,
          [this.getComponentClass(this.objectModelTrigger.trigger_type)]: true,
        };
      },
      isMoveDestroyable(): boolean {
        return this.objectModelTrigger.trigger_type !== ObjectModelTriggerType.DefaultTrigger;
      },
      objectModelPropertyLabel(){
        const firstTrigger = this.objectModelTrigger.first_trigger;

        if (firstTrigger.property_client_id) {
          return this.selectedObjectModel.objectModelPropertiesArray.find(x => x.client_id === firstTrigger.property_client_id)?.label_title
        } else if (firstTrigger.builtin_property_type) {
          return ObjectModelBuiltinPropertyTypeName[firstTrigger.builtin_property_type];
        }
      },
      triggerStartDate(){
        return DatetimeHelper.formatDate(
          DatetimeHelper.parseDate(this.objectModelTrigger.first_trigger.datetime),
          DatetimeHelper.timezone,
          DatetimeHelper.displayFormat(DatetimeMode.Date),
        );
      }
    },
    methods: {
      selectThisItem(event) {
        if (!this.objectModelTrigger.isSelected && this.$route.query['selectedId'] === this.objectModelTrigger.client_id) {
          if (document.activeElement) {
            const $activeEl = $(document.activeElement);
            const $objectModelTrigger = $activeEl.closest('.object-model-trigger');
            if ($objectModelTrigger[0] !== this.$el) {
              $(document.activeElement).blur();
            }
          }
          GloballySelected.objectModelTrigger.set(this.objectModelTrigger);
        }
      },
      durationUnitLabelFor(durationUnit){
        return DurationAndTimeHelper.durationUnitOptions.find(x => x.value === durationUnit)?.label
      },
      comparisonLabelFor(comparisonEnum){
        return statusActivationDateComparisonVOptions.find(x => x.value === comparisonEnum)?.label
      },
      replaceIdInRoute(): void {
        if (!this.disabled) {
          this.replaceRouteQuery({selectedId: this.objectModelTrigger.client_id});
        }
      },
      getComponentByType(type: ObjectModelTriggerType): string {
        return ObjectModelTriggerDefinition.getComponent(type);
      },
      getComponentClass(type: ObjectModelTriggerType): string {
        return ObjectModelTriggerDefinition.getComponentClass(type);
      },
      remove(event): void {
        this.$emit('remove', event);
      },
      scrollToElement(): void {
        scrollToElement({
          $el: $(this.$el),
          $container: $(this.$el).closest('.work-area, .scroll-area'),
          relative: true,
        });
      },
    },
    watch: {
      $route(to, from) {
        if (this.$route.query['selectedId'] === this.objectModelTrigger.client_id) {
          this.selectThisItem();
          this.$nextTick(() =>{
            this.scrollToElement();
          });
        }
      }
    },
  });
</script>